import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useDIContext } from '@/Framework/DI/DIContext';
import CatalogRepository from '@/finsight/infrastructure/repository/CatalogRepository';
import ICatalogCollection from '../common';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import { getErrorMessage } from '@/Framework/Message/Mapper/getMessage';

export interface IIssuersCatalogContext {
  reset: Function,
  isFetching: boolean,
  issuersList: ICatalogCollection,
}

export const CatalogIssuersContext = createContext(null);

export const useIssuers = (serverIssuers: ICatalogCollection): IIssuersCatalogContext => {
  const [issuersList, setIssuersList] = useState<ICatalogCollection>(serverIssuers);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const { container } = useDIContext();
  const getIssuersPageData = async () => {
    setIsFetching(true);
    try {
      const requestService = container.get(CatalogRepository);
      const frontendIssuers: ICatalogCollection = await requestService.getIssuersCatalog();
      setIssuersList(frontendIssuers);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
      setIssuersList({});
    } finally {
      setIsFetching(false);
    }
  };

  const reset = useCallback(() => {
    setIssuersList({});
  }, []);

  useEffect(() => {
    if (!Object.keys(serverIssuers).length) {
      getIssuersPageData();
    }
  }, []);
  return {
    reset,
    isFetching,
    issuersList,
  };
};

export const useCatalogIssuersContext = (): IIssuersCatalogContext => {
  const context = useContext(CatalogIssuersContext);
  if (!context) {
    throw new Error('useCatalogIssuersContext must be used within the CatalogIssuersContext');
  }
  return context;
};

export const CatalogIssuersProvider = ({ children, issuers }) => (
  <CatalogIssuersContext.Provider value={ useIssuers(issuers) }>
    { children }
  </CatalogIssuersContext.Provider>
);
